import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { FaPlus, FaClock, FaCheckCircle, FaExclamationCircle, FaStar, FaCrown } from "react-icons/fa";
import { IoArrowForward } from "react-icons/io5";
import UserLayout from "../DesignLayout/UserLayout";
import { Business } from "../Components/types";
import baseURL from "../config";

const MyBusinesses: React.FC = () => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();

  const fetchBusinesses = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${baseURL}/api/user/my-businesses/${userId}`);
      console.log("Businesses fetched:", res.data);
      setBusinesses(res.data.businesses || []);
    } catch (err: any) {
      console.error("Failed to load businesses:", err);
      if (err.response && err.response.status === 404) {
        setBusinesses([]); // No businesses found is fine
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, [userId]);

  // UI Helper for Status Badges
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <span className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold">
            <FaCheckCircle /> Approved
          </span>
        );
      case "rejected":
        return (
          <span className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-semibold">
            <FaExclamationCircle /> Rejected
          </span>
        );
      default: // pending
        return (
          <span className="flex items-center gap-1 bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-semibold">
            <FaClock /> Pending Approval
          </span>
        );
    }
  };


  return (
    <UserLayout>
      <div className="p-4 sm:p-6 min-h-screen bg-gray-50 pb-24">

        {/* 🏷️ HEADER SECTION */}
        <div className="bg-[#3F87DF] rounded-xl p-6 text-white shadow-lg mb-6 relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-2xl font-bold mb-1">My Business Listings</h2>
            <p className="text-blue-100 text-sm mb-4">Manage your businesses and check approval status.</p>

            <button
              onClick={() => navigate("/user/add-business")}
              className="bg-white text-[#3F87DF] px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 hover:bg-gray-100 transition shadow-md"
            >
              <FaPlus /> Add New Business
            </button>
          </div>
          {/* Decorative Background Icon */}
          <div className="absolute right-0 bottom-0 opacity-10">
            <svg width="150" height="150" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2V9h-2V7h8v12zm-4-8h-2v2h2V7zm0 4h-2v2h2v-2z" />
            </svg>
          </div>
        </div>


        {/* 📋 BUSINESS LIST */}
        {loading ? (
          <div className="grid gap-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 flex gap-3 mb-3 animate-pulse">
                <div className="w-24 h-24 flex-shrink-0 bg-gray-200 rounded-md"></div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-3"></div>
                    <div className="flex items-center gap-2">
                      <div className="h-4 bg-gray-200 rounded w-12"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="h-6 bg-gray-200 rounded w-20"></div>
                    <div className="h-6 bg-gray-200 rounded w-20"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
            <img src="https://cdni.iconscout.com/illustration/premium/thumb/empty-state-2130362-1800926.png" alt="Empty" className="w-40 mx-auto opacity-50 mb-3" />
            <h3 className="text-lg font-semibold text-gray-700">No Businesses Found</h3>
            <p className="text-sm text-gray-500 mb-4">You haven't listed any business yet.</p>
            <button
              onClick={() => navigate("/user/add-business")}
              className="text-[#3F87DF] font-semibold hover:underline"
            >
              + Add your first business
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {businesses.map((biz) => (
              <div
                key={biz._id}
                className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 flex gap-3 mb-3 relative overflow-hidden group cursor-pointer hover:shadow-md hover:border-blue-200 transition-all duration-200"
                onClick={() => navigate(`/business/${biz._id}`)}
              >
                {/* 1. Image Section */}
                <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden relative">
                  {biz.images && biz.images.length > 0 ? (
                    <img
                      src={`${baseURL}/uploads/business/${biz.images[0]}`}
                      alt={biz.businessName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs text-center p-1">No Image</div>
                  )}
                  {/* Status Overlay on Image (Optional, or keep separate) */}
                </div>

                {/* 2. Content Section */}
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start">
                      <h3 className="text-base font-bold text-gray-800 leading-tight line-clamp-2 pr-6">
                        {biz.businessName}
                      </h3>
                      {/* Action Button (Forward/Edit) */}
                      <button className="text-gray-400 hover:text-[#326CB2] transition -mt-1 -mr-1 p-1">
                        <IoArrowForward size={18} />
                      </button>
                    </div>

                    <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">
                      {biz.city} • {biz.category?.name || "Uncategorized"}
                    </p>

                    {/* Rating */}
                    {(biz.rating || biz.ratingCount) ? (
                      <div className="flex items-center gap-2 mt-1">
                        {biz.rating ? (
                          <div className="bg-[#24a148] text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                            {biz.rating} <FaStar size={8} />
                          </div>
                        ) : null}
                        {biz.ratingCount ? (
                          <span className="text-[10px] text-gray-400">{biz.ratingCount} Ratings</span>
                        ) : null}
                      </div>
                    ) : null}

                    {/* Rejection Reason */}
                    {biz.approvalStatus === 'rejected' && biz.rejectionReason && (
                      <p className="text-[10px] text-red-500 mt-1 line-clamp-1">
                        Reason: {biz.rejectionReason}
                      </p>
                    )}
                  </div>

                  {/* Bottom Row: Status Badge + Paid Badge */}
                  <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                    {getStatusBadge(biz.approvalStatus || 'pending')}

                    {/* Paid / Free Badge */}
                    {biz.isPaid ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="flex items-center gap-1 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border border-amber-200 px-2 py-1 rounded text-xs font-bold">
                          <FaCrown size={10} className="text-amber-500" /> Paid
                        </span>
                        {biz.paidExpiry && (
                          <span className={`text-[10px] font-medium ${new Date(biz.paidExpiry) < new Date() ? 'text-red-500' : 'text-emerald-600'}`}>
                            {new Date(biz.paidExpiry) < new Date()
                              ? `Expired: ${new Date(biz.paidExpiry).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
                              : `Expires: ${new Date(biz.paidExpiry).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
                            }
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="flex items-center gap-1 bg-gray-100 text-gray-500 px-2 py-1 rounded text-xs font-medium border border-gray-200">
                        Free Listing
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </UserLayout>
  );
};

export default MyBusinesses;
